import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { formatters, TRANSFER_TYPES } from 'common';
import { TRANSFER_DIRECTIONS } from 'services/search';
import { IS_INCOMING_WIRE } from 'services/details';
import DetailsPanel from '../DetailsPanel';
import styles from './styles.css';
import messages from './messages';
import sourceTypeCopy from './sourceTypeCopy';
import DefListColumn from '../DefListColumn';
import TransferPanelAchDetails from '../TransferPanelAchDetails';
import TransferPanelJournalDetails from '../TransferPanelJournalDetails';
import TransferPanelWireDetails from '../TransferPanelWireDetails';
import TransferPanelIncomingWireDetails from '../TransferPanelWireDetails/TransferPanelIncomingWireDetails';
import TransferPanelCheckDetails from '../transferPanelCheckDetails';
import TransferPanelFeesDetails from '../transferPanelFeesDetails';
import TransferPanelCreditDetails from '../TransferPanelCreditDetails';
import TransferPanelPositionJournal from '../TransferPanelPositionJournal';
import TransferPanelPayPal from '../TransferPanelPayPal';

const TransferPanel = ({
  id,
  transferState,
  amount,
  bankAccountNumber,
  bankAccountIdentifierValue,
  isFullDisbursement,
  instructionSource,
  requestedAmount,
  dateRequested,
  direction,
  achRelationshipEstablishedDate,
  method,
  contraAccount,
  journalType,
  programName,
  payor,
  payorAddress,
  beneficiary,
  beneficiaryAddress,
  destination,
  destinationAddress,
  furtherCreditTo,
  ctsId,
  bankConfirmation,
  checkCtsId,
  checkPayer,
  checkPayerAddress,
  checkBeneficiary,
  checkBeneficiaryAddress,
  checkDeliveryMethod,
  checkMemo,
  feeContraAccount,
  feeType,
  feeContraAccountSideDescription,
  feeAccountSideDescription,
  creditContraAccount,
  creditType,
  creditAccountSideDescription,
  creditContraAccountSideDescription,
  fedWireReferenceNumber,
  externalId,
  houseBankAccountNumber,
  wireFees,
  showAllFields,
  isDomestic,
  isThirdParty,
  wireIsReturn,
  updateWireDetails,
  isFirstPartyJournal,
  transferAccountValue,
  wireUpdateFailure,
  payPalStatus,
  payPalRelationshipId,
  payPalEstablishedDate,
  positionDetails,
  sourceType,
  rtpTransactionId,
  checkBeneficiaryMailingAddress,
  showMailingAddressModal,
  hideMailingAddressModal,
  showMailingAddressModalState,
  mailingAddressError,
}) => (
  <DetailsPanel
    title={
      <div className={styles.title}>
        <span className={styles.halfWidthTitle}>
          {IS_INCOMING_WIRE(method, direction) && <FormattedMessage {...messages.transferDetails} />}
          {!IS_INCOMING_WIRE(method, direction) && <FormattedMessage {...messages.transfer} />}
        </span>

        {method === TRANSFER_TYPES.ach && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.achRelationships} />
          </span>
        )}
        {method === TRANSFER_TYPES.wire && !IS_INCOMING_WIRE(method, direction) && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.wireDetails} />
          </span>
        )}
        {method === TRANSFER_TYPES.check && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.checkDetails} />
          </span>
        )}
        {method === TRANSFER_TYPES.fee && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.feeDetails} />
          </span>
        )}
        {method === TRANSFER_TYPES.credit && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.creditDetails} />
          </span>
        )}
        {method === TRANSFER_TYPES.paypal && (
          <span className={styles.halfWidthTitle} data-test-handle="paypal-title">
            <FormattedMessage {...messages.paypal} />
          </span>
        )}
        {method === TRANSFER_TYPES.positionJournal && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.positionDetails} />
          </span>
        )}
        {method === TRANSFER_TYPES.rtp && (
          <span className={styles.halfWidthTitle}>
            <FormattedMessage {...messages.rtp} />
          </span>
        )}
      </div>
    }
  >
    <div className={styles.panel}>
      {IS_INCOMING_WIRE(method, direction) && (
        <TransferPanelIncomingWireDetails
          transferId={id}
          transferState={transferState}
          dateRequested={dateRequested}
          amount={amount}
          fees={wireFees}
          fedWireReferenceNumber={fedWireReferenceNumber}
          externalId={externalId}
          houseBankAccountNumber={houseBankAccountNumber}
          wireIsDomestic={isDomestic}
          wireIsThirdParty={isThirdParty}
          wireIsReturn={wireIsReturn}
          ShouldUpdateWireDetails={updateWireDetails}
          transferAccountValue={transferAccountValue}
          wireUpdateFailure={wireUpdateFailure}
        />
      )}
      {!IS_INCOMING_WIRE(method, direction) && (
        <React.Fragment>
          <div className={styles.leftPane}>
            <DefListColumn
              className={styles.defList}
              title={<FormattedMessage {...messages.transferId} />}
              data={id}
              dataTestHandle="transferPanel__transferId"
            />
            {method === TRANSFER_TYPES.rtp && (
              <DefListColumn
                className={styles.defList}
                title={<FormattedMessage {...messages.rtpTransactionId} />}
                data={rtpTransactionId}
                dataTestHandle="transferPanel__rtpTransactionId"
              />
            )}

            {method !== TRANSFER_TYPES.positionJournal && (
              <React.Fragment>
                <DefListColumn
                  className={styles.defList}
                  title={<FormattedMessage {...messages.amount} />}
                  data={formatters.formatDollars(amount)}
                  dataTestHandle="transferPanel__amount"
                />
                <DefListColumn
                  className={styles.defList}
                  title={<FormattedMessage {...messages.requestedAmount} />}
                  data={formatters.formatDollars(requestedAmount)}
                  dataTestHandle="transferPanel__requestedAmount"
                />
              </React.Fragment>
            )}
            {direction !== TRANSFER_DIRECTIONS.transferDirectionDeposit &&
              method !== TRANSFER_TYPES.positionJournal && (
                <DefListColumn
                  className={styles.defList}
                  title={<FormattedMessage {...messages.disbursementType} />}
                  data={
                    isFullDisbursement ? (
                      <FormattedMessage {...messages.full} />
                    ) : (
                      <FormattedMessage {...messages.partial} />
                    )
                  }
                  dataTestHandle="transferPanel__disbursementType"
                />
              )}
            <DefListColumn
              className={styles.defList}
              title={<FormattedMessage {...messages.source} />}
              data={sourceTypeCopy[sourceType] || sourceType}
              dataTestHandle="transferPanel__source"
            />
            {showAllFields && (
              <DefListColumn
                className={styles.longDataDefList}
                title={<FormattedMessage {...messages.instructionSource} />}
                // Default to showing the raw value in case the backend adds a new instruction source.
                data={formatters.formatInstructionSource(instructionSource)}
                dataTestHandle="transferPanel__instructionSource"
              />
            )}
            <DefListColumn
              className={styles.defList}
              title={<FormattedMessage {...messages.dateRequested} />}
              data={dateRequested}
              dataTestHandle="transferPanel__dateRequested"
            />
            {method === TRANSFER_TYPES.positionJournal && (
              <React.Fragment>
                <DefListColumn
                  className={styles.defList}
                  title={<FormattedMessage {...messages.journalType} />}
                  data={positionDetails.journalType}
                  dataTestHandle="transferPanel__journalType"
                />
                <DefListColumn
                  className={styles.defList}
                  title={<FormattedMessage {...messages.destinationAccount} />}
                  data={positionDetails.destinationAccountNumber}
                  dataTestHandle="transferPanel__destinationAccount"
                />
              </React.Fragment>
            )}
          </div>
          {method === TRANSFER_TYPES.journal && (
            <TransferPanelJournalDetails
              contraAccount={contraAccount}
              journalType={journalType}
              programName={programName}
              isFirstPartyJournal={isFirstPartyJournal}
            />
          )}
          {(method === TRANSFER_TYPES.ach || method === TRANSFER_TYPES.rtp) && (
            <TransferPanelAchDetails
              bankAccountNumber={bankAccountNumber}
              bankAccountIdentifierValue={bankAccountIdentifierValue}
              establishedDate={achRelationshipEstablishedDate}
            />
          )}
          {method === TRANSFER_TYPES.wire && (
            <TransferPanelWireDetails
              showAllFields={showAllFields}
              ctsId={ctsId}
              bankConfirmation={bankConfirmation}
              payor={payor}
              payorAddress={payorAddress}
              beneficiary={beneficiary}
              beneficiaryAddress={beneficiaryAddress}
              destination={destination}
              destinationAddress={destinationAddress}
              furtherCreditTo={furtherCreditTo}
              fedWireReferenceNumber={fedWireReferenceNumber}
              externalId={externalId}
            />
          )}
          {method === TRANSFER_TYPES.check && (
            <TransferPanelCheckDetails
              showAllFields={showAllFields}
              ctsId={checkCtsId}
              payer={checkPayer}
              payerAddress={checkPayerAddress}
              beneficiary={checkBeneficiary}
              beneficiaryAddress={checkBeneficiaryAddress}
              deliveryMethod={checkDeliveryMethod}
              memo={checkMemo}
              checkBeneficiaryMailingAddress={checkBeneficiaryMailingAddress}
              showMailingAddressModal={showMailingAddressModal}
              hideMailingAddressModal={hideMailingAddressModal}
              showMailingAddressModalState={showMailingAddressModalState}
              mailingAddressError={mailingAddressError}
            />
          )}
          {method === TRANSFER_TYPES.fee && (
            <TransferPanelFeesDetails
              contraAccount={feeContraAccount}
              type={feeType}
              accountSideDescription={feeAccountSideDescription}
              contraAccountSideDescription={feeContraAccountSideDescription}
            />
          )}
          {method === TRANSFER_TYPES.credit && (
            <TransferPanelCreditDetails
              contraAccount={creditContraAccount}
              type={creditType}
              accountSideDescription={creditAccountSideDescription}
              contraAccountSideDescription={creditContraAccountSideDescription}
            />
          )}
          {method === TRANSFER_TYPES.paypal && (
            <TransferPanelPayPal
              relationshipId={payPalRelationshipId}
              status={payPalStatus}
              establishedDate={payPalEstablishedDate}
            />
          )}
          {method === TRANSFER_TYPES.positionJournal && (
            <TransferPanelPositionJournal
              quantity={positionDetails.quantity}
              securityId={positionDetails.securityId}
              securityIdType={positionDetails.securityIdType}
              price={amount}
            />
          )}
        </React.Fragment>
      )}
    </div>
  </DetailsPanel>
);

TransferPanel.propTypes = {
  id: PropTypes.string.isRequired,
  transferState: PropTypes.string.isRequired,
  accountOpenedDate: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  bankAccountNumber: PropTypes.string.isRequired,
  bankAccountIdentifierValue: PropTypes.string.isRequired,
  isFullDisbursement: PropTypes.bool.isRequired,
  instructionSource: PropTypes.string || null,
  correspondentName: PropTypes.string.isRequired,
  requestedAmount: PropTypes.number.isRequired,
  dateRequested: PropTypes.string.isRequired,
  direction: PropTypes.string.isRequired,
  achRelationshipEstablishedDate: PropTypes.string.isRequired,
  method: PropTypes.string.isRequired,
  contraAccount: PropTypes.string.isRequired,
  journalType: PropTypes.string.isRequired,
  programName: PropTypes.string.isRequired,
  ctsId: PropTypes.number || null,
  bankConfirmation: {
    id: PropTypes.string,
    type: PropTypes.string,
  } || null,
  payor: PropTypes.object,
  payorAddress: PropTypes.string,
  beneficiary: PropTypes.object,
  beneficiaryAddress: PropTypes.string,
  destination: PropTypes.object,
  destinationAddress: PropTypes.string,
  furtherCreditTo: PropTypes.object,
  checkCtsId: PropTypes.number || null,
  checkPayer: PropTypes.object,
  checkPayerAddress: PropTypes.string,
  checkBeneficiary: PropTypes.object,
  checkBeneficiaryAddress: PropTypes.string,
  checkDeliveryMethod: PropTypes.string,
  checkMemo: PropTypes.string,
  feeContraAccount: PropTypes.string,
  feeType: PropTypes.string,
  feeContraAccountSideDescription: PropTypes.array,
  feeAccountSideDescription: PropTypes.array,
  creditContraAccount: PropTypes.string,
  creditType: PropTypes.string,
  creditAccountSideDescription: PropTypes.string,
  creditContraAccountSideDescription: PropTypes.string,
  showAllFields: PropTypes.bool.isRequired,
  wireFees: PropTypes.number,
  fedWireReferenceNumber: PropTypes.string,
  externalId: PropTypes.string,
  houseBankAccountNumber: PropTypes.string,
  isDomestic: PropTypes.bool,
  isThirdParty: PropTypes.bool,
  wireIsReturn: PropTypes.bool,
  updateWireDetails: PropTypes.bool.isRequired,
  isFirstPartyJournal: PropTypes.bool.isRequired,
  transferAccountValue: PropTypes.string,
  wireUpdateFailure: PropTypes.bool,
  payPalStatus: PropTypes.string,
  payPalRelationshipId: PropTypes.string,
  payPalEstablishedDate: PropTypes.string,
  positionDetails: PropTypes.shape({
    quantity: PropTypes.number.isRequired,
    securityIdType: PropTypes.string.isRequired,
    securityId: PropTypes.string.isRequired,
    price: PropTypes.number,
    journalType: PropTypes.string.isRequired,
    destinationAccountNumber: PropTypes.string.isRequired,
  }),
  rtpTransactionId: PropTypes.string,
  sourceType: PropTypes.string,
  checkBeneficiaryMailingAddress: PropTypes.object,
  showMailingAddressModal: PropTypes.func.isRequired,
  hideMailingAddressModal: PropTypes.func.isRequired,
  showMailingAddressModalState: PropTypes.bool.isRequired,
  mailingAddressError: PropTypes.object,
};

TransferPanel.defaultProps = {
  ctsId: null,
  bankConfirmation: null,
  payor: null,
  payorAddress: '',
  beneficiary: null,
  beneficiaryAddress: '',
  destination: null,
  destinationAddress: '',
  furtherCreditTo: null,
  checkCtsId: null,
  checkPayer: null,
  checkPayerAddress: null,
  checkBeneficiary: null,
  checkBeneficiaryAddress: null,
  checkBeneficiaryMailingAddress: null,
  checkDeliveryMethod: '',
  checkMemo: '',
  feeContraAccount: '',
  feeType: '',
  feeContraAccountSideDescription: [],
  feeAccountSideDescription: [],
  creditContraAccount: '',
  creditType: '',
  creditAccountSideDescription: '',
  creditContraAccountSideDescription: '',
  instructionSource: null,
  wireFees: null,
  fedWireReferenceNumber: '',
  externalId: '',
  houseBankAccountNumber: '',
  isDomestic: null,
  isThirdParty: null,
  wireIsReturn: false,
  transferAccountValue: null,
  wireUpdateFailure: null,
  payPalStatus: null,
  payPalRelationshipId: null,
  payPalEstablishedDate: null,
  positionDetails: null,
  rtpTransactionId: null,
  sourceType: '',
  mailingAddressError: null,
};

export default TransferPanel;



// WEBPACK FOOTER //
// ./src/components/TransferPanel/index.js